import { AppModule } from 'app/module';
import { Textarea } from 'module/textarea/main';
import { TextareaCounter } from 'module/textareaCounter/main';
import { ValidationMessageBase } from 'module/validationMessage/main';
import { userWatchlist } from 'app/userWatchlist';
import Template from './template.hbs';
import './styles.scss';

const textareaCharLimit = 500;

export class Memo extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    ready() {
        setTimeout(() => {
            this.events.emit('showMemoOverlay');
        }, 5);
        userWatchlist.fetchAll();
        userWatchlist.events.on('onResponse', () => this.update());
        userWatchlist.events.on('onWatchlistEntryResponse', () => this.update());
        userWatchlist.events.on('onWatchlistEntryResponseError', (e) => this.handleWatchlistResponseError(e));
    }

    domBindings() {
        this.dom.textareaContainer = this.dom.el.querySelector('.m-memo__textarea .m-textarea');
        this.dom.textarea = this.dom.el.querySelector('.m-memo__textarea .m-textarea .m-textarea__textarea');
        this.dom.textareaCounter = this.dom.el.querySelector('.m-memo__memoCounter .m-textareaCounter');
        this.dom.abortButton = this.dom.el.querySelector('.m-memo__abort');
        this.dom.saveButton = this.dom.el.querySelector('.m-memo__save');
        this.dom.deleteButton = this.dom.el.querySelector('.m-memo__delete');
        this.dom.jobId = this.dom.el.querySelector('.m-memo__jobId');
        this.dom.textareaValidationMessage = this.dom.el.querySelector('.m-memo__validationMessage');

        if (this.dom.textareaContainer) {
            this.textareaContainer = new Textarea(this.dom.textareaContainer);
        }
        if (this.dom.textareaCounter) {
            this.textareaCounter = new TextareaCounter(this.dom.textareaCounter);
        }
        if (this.dom.textareaValidationMessage) {
            this.textareaValidationMessage = new ValidationMessageBase(this.dom.textareaValidationMessage);
        }
    }

    getPropsFromDom() {
        return {
            jobId: this.dom.jobId ? this.dom.jobId.value : '',
            textareaProps: this.textareaContainer.getPropsFromDom(),
        };
    }

    domEvents() {
        this.dom.abortButton.addEventListener('click', () => {
            this.update();
        });

        if (this.dom.saveButton) {
            this.dom.saveButton.addEventListener('click', (e) => this.saveMemo(e));
        }

        if (this.dom.deleteButton) {
            this.dom.deleteButton.addEventListener('click', () => {
                this.events.emit('openDeleteModal');
            });
        }
    }

    subEvents() {
        if (this.textareaContainer) {
            this.textareaContainer.events.on('textareaInputChanged', () => {
                this.textareaContainer.textareaGrow();
                this.updateMemoComponentStatus();
            });
        }
    }

    update() {
        const watchlistEntry = userWatchlist.getWatchlistEntry(this.props.jobId);
        const value = watchlistEntry?.memo ?? '';
        this.textareaContainer.updateProps({ value }, true);
        this.textareaContainer.textareaGrow();
        this.updateMemoComponentStatus();
        this.events.emit('hideMemoOverlay');
    }

    saveMemo(e) {
        if (e) e.preventDefault();
        if (e) e.stopPropagation();

        if (!this.validate() || this.memoContentSimilar()) return;

        this.textareaContainer.setValid();

        const watchlistData = {
            jobId: this.props.jobId,
            watchlistId: this.getWatchlistId(this.props.jobId),
            memo: this.textareaContainer.getValue(),
        };
        this.updateMemoComponentStatus();
        userWatchlist.saveWatchlistEntry(watchlistData)
            .then(() => {
                this.events.emit('showSnackbar');
                this.events.emit('memoSaved', this.props.jobId);
            })
            .catch(() => {});
    }

    async deleteMemo() {
        const watchlistData = {
            jobId: this.props.jobId,
            watchlistId: this.getWatchlistId(this.props.jobId),
            memo: '',
        };
        await userWatchlist.saveWatchlistEntry(watchlistData);
        this.events.emit('memoDeleted', this.props.jobId);
    }

    getWatchlistId(jobId) {
        return userWatchlist.getWatchlistEntry(jobId).watchlistId;
    }

    memoContentSimilar() {
        return this.textareaContainer.getValue() === userWatchlist.getWatchlistEntry(this.props.jobId)?.memo;
    }

    validationRules() {
        this.validation.after(({ valid }) => {
            if (!valid) this.textareaContainer.highlightText(textareaCharLimit);
        });
        this.validation.addRule(this.textareaContainer, {
            rules: [{
                name: `max:${textareaCharLimit}`,
                test: (value) => value.length <= textareaCharLimit,
                message: (value) => `Du kannst nur ${value} Zeichen eingeben.`,
            }],
            validationMessageOutlet: this.textareaValidationMessage,
        });
    }

    updateMemoComponentStatus() {
        const textareaValueLength = this.getTextareaValue().length;

        if (this.textareaCounter) {
            this.textareaCounter.updateCount(textareaValueLength);
        }
        this.dom.abortButton.disabled = this.memoContentSimilar();
        this.dom.saveButton.disabled = this.memoContentSimilar();

        // Grab the cursor position, because after the textarea gets set (in)valid it gets rendered.
        // This means we loose the focus and the current cursor position.
        const currentCursorPos = this.textareaContainer.getCurrentCursorPos();
        this.validate();

        if (textareaValueLength === 0) {
            this.dom.deleteButton.classList.add('m-memo__delete--hide');
        } else if (this.dom.deleteButton.classList.contains('m-memo__delete--hide')) {
            this.dom.deleteButton.classList.remove('m-memo__delete--hide');
        }

        this.focusTextarea(currentCursorPos);
    }

    getTextareaValue() {
        return this.textareaContainer.getValue();
    }

    focusTextarea(currentCursorPos) {
        this.textareaContainer.setFocus();
        this.textareaContainer.setCurrentCursorPos(currentCursorPos);
    }

    handleWatchlistResponseError(e) {
        this.validation.handleBackendValidationErrors(e.arguments[0]);
    }
}
